import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle, 
  X, 
  Calendar,
  DollarSign,
  Bell,
  BellOff
} from 'lucide-react';
import { format, isAfter, isBefore, startOfDay, addDays, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatCurrency';

const PaymentAlerts = ({ 
  expenses = [], 
  onMarkAsPaid, 
  onDismissAlert,
  showNotifications = true,
  onToggleNotifications,
  readOnly = false
}) => {
  const [alerts, setAlerts] = useState([]);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  
  // Função para calcular próximo vencimento
  const getNextDueDate = (dueDay) => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Tenta criar data no mês atual
    let nextDue = new Date(currentYear, currentMonth, dueDay);
    
    // Se já passou, vai para o próximo mês
    if (isBefore(nextDue, startOfDay(today))) {
      nextDue = new Date(currentYear, currentMonth + 1, dueDay);
    }
    
    return nextDue;
  };
  
  // Função para verificar se foi pago no mês atual
  const isPaidThisMonth = (expense) => {
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    
    return expense.paymentHistory?.some(payment => {
      const paymentDate = new Date(payment.paidDate);
      return paymentDate.getMonth() === currentMonth && 
             paymentDate.getFullYear() === currentYear;
    });
  };
  
  // Calcular alertas
  useEffect(() => {
    const today = startOfDay(new Date());
    const newAlerts = [];
    
    expenses.forEach(expense => {
      // Pular se já foi pago este mês
      if (isPaidThisMonth(expense)) return;
      
      const nextDue = getNextDueDate(expense.dueDay);
      const daysUntilDue = differenceInDays(nextDue, today);
      const alertDays = expense.alertDaysBefore || 3;
      
      let alertType = null;
      let priority = 0;
      
      if (daysUntilDue < 0) {
        // Vencido
        alertType = 'overdue';
        priority = 3;
      } else if (daysUntilDue === 0) {
        // Vence hoje
        alertType = 'due_today';
        priority = 2;
      } else if (daysUntilDue <= alertDays) {
        // Próximo ao vencimento
        alertType = 'due_soon';
        priority = 1;
      }
      
      if (alertType) {
        const alertId = `${expense._id}-${format(nextDue, 'yyyy-MM')}`;
        
        // Não mostrar alertas dispensados
        if (!dismissedAlerts.has(alertId)) {
          newAlerts.push({
            id: alertId,
            expense,
            type: alertType,
            priority,
            dueDate: nextDue,
            daysUntilDue
          });
        }
      }
    });
    
    // Ordenar por prioridade (maior primeiro) e depois por dias até vencimento
    newAlerts.sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.daysUntilDue - b.daysUntilDue;
    });
    
    setAlerts(newAlerts);
  }, [expenses, dismissedAlerts]);
  
  
  const getAlertIcon = (type) => {
    switch (type) {
      case 'overdue':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'due_today':
        return <Clock className="w-5 h-5 text-orange-500" />;
      case 'due_soon':
        return <Calendar className="w-5 h-5 text-yellow-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };
  
  const getAlertColor = (type) => {
    switch (type) {
      case 'overdue':
        return 'border-red-200 bg-red-50';
      case 'due_today':
        return 'border-orange-200 bg-orange-50';
      case 'due_soon':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-gray-200 bg-gray-50';
    }
  };
  
  const getAlertTitle = (alert) => {
    switch (alert.type) {
      case 'overdue':
        return `Vencido há ${Math.abs(alert.daysUntilDue)} dia(s)`;
      case 'due_today':
        return 'Vence hoje';
      case 'due_soon':
        return `Vence em ${alert.daysUntilDue} dia(s)`;
      default:
        return 'Alerta';
    }
  };
  
  const handleDismissAlert = (alertId) => {
    setDismissedAlerts(prev => new Set([...prev, alertId]));
    if (onDismissAlert) {
      onDismissAlert(alertId);
    }
  };
  
  const handleMarkAsPaid = (expense) => {
    if (onMarkAsPaid) {
      onMarkAsPaid(expense);
    }
  };
  
  if (!showNotifications || alerts.length === 0) {
    return null;
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-orange-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Alertas de Pagamento ({alerts.length})
          </h3>
        </div>
        
        {onToggleNotifications && (
          <button
            onClick={onToggleNotifications}
            className="btn btn-secondary btn-sm flex items-center gap-2"
            title={showNotifications ? 'Desativar notificações' : 'Ativar notificações'}
          >
            {showNotifications ? (
              <BellOff className="w-4 h-4" />
            ) : (
              <Bell className="w-4 h-4" />
            )}
            {showNotifications ? 'Desativar' : 'Ativar'}
          </button>
        )}
      </div>
      
      {/* Lista de alertas */}
      <div className="space-y-3">
        {alerts.map(alert => (
          <div
            key={alert.id}
            className={`border rounded-lg p-4 ${getAlertColor(alert.type)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3 flex-1">
                {getAlertIcon(alert.type)}
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">
                      {alert.expense.name}
                    </h4>
                    <span className="text-xs px-2 py-1 bg-white rounded-full text-gray-600">
                      {alert.expense.category}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2">
                    {getAlertTitle(alert)} • {formatCurrency(alert.expense.amount)}
                  </p>
                  
                  <p className="text-xs text-gray-500">
                    Vencimento: {format(alert.dueDate, "dd 'de' MMMM", { locale: ptBR })}
                  </p>
                  
                  {/* Ações */}
                  <div className="flex items-center gap-2 mt-3">
                    {!readOnly && (
                      <button
                        onClick={() => { handleMarkAsPaid(alert.expense); handleDismissAlert(alert.id); }}
                        className="btn btn-success btn-sm flex items-center gap-1"
                      >
                        <CheckCircle className="w-3 h-3" />
                        Marcar como Pago
                      </button>
                    )}
                    <button
                      onClick={() => handleDismissAlert(alert.id)}
                      className="btn btn-secondary btn-sm"
                    >
                      Dispensar
                    </button>
                  </div>
                </div>
              </div>
              
              <button
                onClick={() => handleDismissAlert(alert.id)}
                className="text-gray-400 hover:text-gray-600 transition-colors ml-2"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Resumo */}
      {alerts.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-900">
              Resumo dos Alertas
            </span>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-red-600 font-medium">
                {alerts.filter(a => a.type === 'overdue').length}
              </span>
              <span className="text-gray-600 ml-1">Vencidas</span>
            </div>
            
            <div>
              <span className="text-orange-600 font-medium">
                {alerts.filter(a => a.type === 'due_today').length}
              </span>
              <span className="text-gray-600 ml-1">Hoje</span>
            </div>
            
            <div>
              <span className="text-yellow-600 font-medium">
                {alerts.filter(a => a.type === 'due_soon').length}
              </span>
              <span className="text-gray-600 ml-1">Próximas</span>
            </div>
            
            <div>
              <span className="text-blue-600 font-medium">
                {formatCurrency(
                  alerts.reduce((sum, alert) => sum + alert.expense.amount, 0)
                )}
              </span>
              <span className="text-gray-600 ml-1">Total</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentAlerts;